/**
 * Template-from-Run Generator
 *
 * Pure async function that takes run data + a content fetcher and produces a
 * validated `AutoTemplate` object so users can generate a reusable template
 * directly from a succeeded run. The output matches the format published to
 * template storage from Azure/ProjectAutoTemplates.
 */

import { isMockableType } from './mockableActions';
import { validateAutoTemplate, type AutoTemplate } from '@/types/autoTemplate';
import { DEFAULT_AUTHORED_TEMPLATE_CATEGORIES } from '@/types/template';
import type { WorkflowDefinition } from '@/types/workflowDefinition';
import type { RunWithOutputLinks } from '@/services/monitoringApi';
import type { ConnectionsConfiguration } from '@/types/logicApps';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TemplateFromRunOptions {
  runId: string;
  workflowName: string;
  workflowDefinition: WorkflowDefinition;
  triggerName: string;
  triggerType: string;
  runWithOutputLinks: RunWithOutputLinks;
  /** Adapter around fetchActionContent — takes a URI, returns parsed JSON. */
  fetchContent: (uri: string) => Promise<unknown>;
  /** Deployed connections.json — used to extract connection requirements. */
  connections: ConnectionsConfiguration | null;
  /** Display name of the signed-in user, used as `metadata.author`. */
  author?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Featured-connector selection priority: in-app service providers first, then
 * managed APIs, then built-in connection providers (e.g. the Request trigger).
 */
function featuredConnectorPriority(apiId: string): number {
  if (apiId.includes('serviceProviders/')) return 0;
  if (apiId.includes('managedApis/')) return 1;
  if (apiId.includes('connectionProviders/')) return 2;
  return 3;
}

/**
 * Pick the default `metadata.featuredConnectors` from the candidate ids: up to
 * three, preferring serviceProviders > managedApis > connectionProviders.
 * Fewer than three candidates are all kept (the schema minimum of one is the
 * caller's responsibility). The sort is stable, so ties keep candidate order.
 */
export function selectDefaultFeaturedConnectors(candidateIds: string[]): string[] {
  return [...new Set(candidateIds)]
    .sort((a, b) => featuredConnectorPriority(a) - featuredConnectorPriority(b))
    .slice(0, 3);
}

/** Convert a workflow name to a URL-safe kebab-case id. */
function toKebabCase(name: string): string {
  return name
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();
}

/**
 * Generalize a template by replacing workflow-name-specific references with
 * the `#workflowname#` placeholder. This is the reverse of the forward
 * substitution in `useTemplateApply.rewriteWorkflowNamePlaceholders`.
 *
 * Replaces `_<workflowName>` → `_#workflowname#` throughout the serialized
 * template JSON — this covers connection keys and expression references inside
 * the definition.
 */
function generalizeWorkflowName<T>(obj: T, workflowName: string): T {
  const suffix = `_${workflowName}`;
  const json = JSON.stringify(obj);
  if (!json.includes(suffix)) return obj;
  return JSON.parse(json.split(suffix).join('_#workflowname#')) as T;
}

/** HTTP-type trigger types whose outputs carry request data. */
const HTTP_TRIGGER_TYPES = new Set([
  'request',
  'httpwebhook',
  'manual',
  'apiconnectionwebhook',
]);

/** Headers that may contain credentials — scrubbed from trigger outputs. */
const SENSITIVE_HEADERS = [
  'authorization',
  'cookie',
  'x-api-key',
  'x-functions-key',
];

/** Header prefixes that may contain credentials. */
const SENSITIVE_HEADER_PREFIXES = [
  'x-ms-client-principal',
  'x-ms-token-aad-',
];

/**
 * Infrastructure/proxy headers that carry no useful sample data and just add
 * noise. Stripped entirely from trigger outputs (not redacted — removed).
 */
const INFRASTRUCTURE_HEADER_PREFIXES = [
  'x-forwarded-',
  'x-envoy-',
  'x-k8se-',
  'x-ms-containerapp-',
  'x-arr-',
  'sec-ch-ua',
  'sec-fetch-',
];

const INFRASTRUCTURE_HEADERS = new Set([
  'host',
  'accept-encoding',
  'accept-language',
  'content-length',
  'priority',
  'origin',
  'referer',
  'user-agent',
  'x-request-id',
]);

/** Query parameters that may contain secrets. */
const SENSITIVE_QUERY_PARAMS = new Set(['code', 'sig', 'sp', 'sv', 'se', 'sr']);

function isSensitiveHeader(key: string): boolean {
  const lower = key.toLowerCase();
  if (SENSITIVE_HEADERS.includes(lower)) return true;
  return SENSITIVE_HEADER_PREFIXES.some((prefix) => lower.startsWith(prefix));
}

function isInfrastructureHeader(key: string): boolean {
  const lower = key.toLowerCase();
  if (INFRASTRUCTURE_HEADERS.has(lower)) return true;
  return INFRASTRUCTURE_HEADER_PREFIXES.some((prefix) => lower.startsWith(prefix));
}

function scrubHeaders(
  headers: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(headers)) {
    // Drop infrastructure headers entirely
    if (isInfrastructureHeader(key)) continue;
    // Redact sensitive headers
    result[key] = isSensitiveHeader(key) ? '***' : value;
  }
  return result;
}

function scrubQueries(
  queries: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(queries)) {
    result[key] = SENSITIVE_QUERY_PARAMS.has(key.toLowerCase()) ? '***' : value;
  }
  return result;
}

// ---------------------------------------------------------------------------
// Step 1: Walk definition to find mockable action names
// ---------------------------------------------------------------------------

type ActionMap = Record<string, { type?: string; actions?: ActionMap; else?: { actions?: ActionMap }; cases?: Record<string, { actions?: ActionMap }>; default?: { actions?: ActionMap }; tools?: Record<string, { actions?: ActionMap }> }>;

function collectMockableActions(
  actions: ActionMap | undefined,
  result: string[],
): void {
  if (!actions) return;
  for (const [name, action] of Object.entries(actions)) {
    if (action.type && isMockableType(action.type)) {
      result.push(name);
    }
    // Recurse into scope actions
    collectMockableActions(action.actions, result);
    // If (.else)
    if (action.else?.actions) {
      collectMockableActions(action.else.actions, result);
    }
    // Switch (.cases + .default)
    if (action.cases) {
      for (const c of Object.values(action.cases)) {
        collectMockableActions(c.actions, result);
      }
    }
    if (action.default?.actions) {
      collectMockableActions(action.default.actions, result);
    }
    // Agent (.tools)
    if (action.tools) {
      for (const tool of Object.values(action.tools)) {
        collectMockableActions(tool.actions, result);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Step 2: Fetch mockable action outputs
// ---------------------------------------------------------------------------

async function fetchMockOutputs(
  mockableActionNames: string[],
  runWithOutputLinks: RunWithOutputLinks,
  fetchContent: (uri: string) => Promise<unknown>,
): Promise<Record<string, { status: 'Succeeded'; outputs: { statusCode: string; body: unknown } }>> {
  const mocks: Record<string, { status: 'Succeeded'; outputs: { statusCode: string; body: unknown } }> = {};

  const entries = mockableActionNames
    .map((name) => {
      const link = runWithOutputLinks.actionOutputLinks[name];
      return link?.uri ? { name, uri: link.uri } : null;
    })
    .filter((e): e is { name: string; uri: string } => e !== null);

  const results = await Promise.allSettled(
    entries.map(async ({ name, uri }) => {
      const content = await fetchContent(uri);
      return { name, content };
    }),
  );

  for (const result of results) {
    if (result.status !== 'fulfilled') continue;
    const { name, content } = result.value;

    // Skip error responses from fetchActionContent
    if (
      content &&
      typeof content === 'object' &&
      'error' in content
    ) {
      console.warn(`[templateFromRun] Skipping mock for "${name}": fetch returned error`, content);
      continue;
    }

    const outputs = content as Record<string, unknown> | undefined;
    if (!outputs) continue;

    // Normalize statusCode: number → string, default "200"
    let statusCode = '200';
    if (outputs.statusCode !== undefined && outputs.statusCode !== null) {
      statusCode = String(outputs.statusCode);
    }

    // Response headers are dropped entirely as part of sanitization: they are
    // connector/transport noise and can carry sensitive values (set-cookie,
    // request ids, resource URLs) that the header scrubber's allow/deny lists
    // can't anticipate. Mock replay only needs statusCode + body.
    mocks[name] = {
      status: 'Succeeded',
      outputs: {
        statusCode,
        body: outputs.body ?? null,
      },
    };
  }

  return mocks;
}

// ---------------------------------------------------------------------------
// Step 3: Fetch and scrub trigger outputs
// ---------------------------------------------------------------------------

interface TriggerOutputs {
  method: string;
  headers: Record<string, unknown>;
  queries: Record<string, unknown>;
  body: unknown;
}

async function fetchTriggerOutputs(
  triggerType: string,
  runWithOutputLinks: RunWithOutputLinks,
  fetchContent: (uri: string) => Promise<unknown>,
): Promise<TriggerOutputs> {
  const defaultOutputs: TriggerOutputs = {
    method: 'POST',
    headers: {},
    queries: {},
    body: null,
  };

  const isHttpTrigger = HTTP_TRIGGER_TYPES.has(triggerType.toLowerCase());

  const triggerLink = runWithOutputLinks.triggerOutputLink;
  if (!triggerLink?.uri) return defaultOutputs;

  try {
    const content = await fetchContent(triggerLink.uri);

    // Skip error responses
    if (content && typeof content === 'object' && 'error' in content) {
      return defaultOutputs;
    }

    if (!isHttpTrigger) return defaultOutputs;

    const raw = content as Record<string, unknown> | undefined;
    if (!raw) return defaultOutputs;

    return {
      method: typeof raw.method === 'string' ? raw.method : 'POST',
      headers: scrubHeaders(
        (raw.headers as Record<string, unknown>) ?? {},
      ),
      queries: scrubQueries(
        (raw.queries as Record<string, unknown>) ?? {},
      ),
      body: raw.body ?? null,
    };
  } catch {
    return defaultOutputs;
  }
}

// ---------------------------------------------------------------------------
// Step 4: Collect connection references used in the definition
// ---------------------------------------------------------------------------

/**
 * Scan the workflow definition to collect all connection reference names
 * actually used. This covers:
 *   - ApiConnection: `inputs.host.connection.referenceName`
 *   - ServiceProvider: `inputs.serviceProviderConfiguration.connectionName`
 *
 * Returns a Set of deployed connection keys so we can filter the app-level
 * `connections.json` (which contains connections for ALL workflows) down to
 * only the ones this workflow needs.
 */
function collectUsedConnectionKeys(definition: Record<string, unknown>): Set<string> {
  const used = new Set<string>();
  const json = JSON.stringify(definition);

  // Match "referenceName":"<value>" patterns
  const refNameRegex = /"referenceName"\s*:\s*"([^"]+)"/g;
  let match: RegExpExecArray | null;
  while ((match = refNameRegex.exec(json)) !== null) {
    used.add(match[1]!);
  }

  // Match "connectionName":"<value>" patterns (ServiceProvider)
  const connNameRegex = /"connectionName"\s*:\s*"([^"]+)"/g;
  while ((match = connNameRegex.exec(json)) !== null) {
    used.add(match[1]!);
  }

  return used;
}

// ---------------------------------------------------------------------------
// Step 5: Extract connections from connections.json
// ---------------------------------------------------------------------------

interface ExtractedConnections {
  /** Template connection entries keyed by canonical `<apiShortName>_#workflowname#`. */
  connections: Record<string, { connectorType: 'shared' | 'inapp' | 'agent'; apiId: string }>;
  /** Maps deployed connection key → canonical template key for definition rewriting. */
  renameMap: Map<string, string>;
}

/**
 * Derive a short connector name from an API ID path.
 *   "/managedApis/msnweather"       → "msnweather"
 *   "/serviceProviders/AzureBlob"   → "AzureBlob"
 */
function apiShortName(apiId: string): string {
  const last = apiId.split('/').filter(Boolean).pop();
  return last ?? 'connector';
}

/**
 * Extract template connection requirements from the deployed `connections.json`.
 *
 * `connections.json` is app-level — it contains connections for ALL workflows
 * in the app. Only connections whose deployed key appears in `usedKeys` (the
 * set of reference names actually used in this workflow's definition) are
 * included.
 *
 * Deployed connection keys are auto-generated (e.g. `msn-weather-01d1`) and
 * don't follow the `<name>_#workflowname#` convention the template schema
 * requires. This function:
 *   1. Filters to only connections referenced by the workflow definition.
 *   2. Generates canonical keys from the API ID (`msnweather_#workflowname#`).
 *   3. Returns a rename map so the caller can rewrite connection references
 *      inside the workflow definition to match.
 */
function extractConnections(
  connections: ConnectionsConfiguration | null,
  usedKeys: Set<string>,
): ExtractedConnections {
  if (!connections) return { connections: {}, renameMap: new Map() };

  const result: Record<string, { connectorType: 'shared' | 'inapp' | 'agent'; apiId: string }> = {};
  const renameMap = new Map<string, string>();
  // Track used canonical bases to disambiguate duplicates (e.g. two SQL connections).
  const usedBases = new Map<string, number>();

  const addEntry = (
    deployedKey: string,
    apiId: string,
    connectorType: 'shared' | 'inapp' | 'agent',
  ) => {
    // Skip connections not referenced by this workflow
    if (!usedKeys.has(deployedKey)) return;

    const base = apiShortName(apiId);
    const count = (usedBases.get(base) ?? 0) + 1;
    usedBases.set(base, count);
    // First occurrence: `sql_#workflowname#`; second: `sql2_#workflowname#`, etc.
    const canonicalKey = count === 1
      ? `${base}_#workflowname#`
      : `${base}${count}_#workflowname#`;

    result[canonicalKey] = { connectorType, apiId };
    renameMap.set(deployedKey, canonicalKey);
  };

  if (connections.managedApiConnections) {
    for (const [key, conn] of Object.entries(connections.managedApiConnections)) {
      if (conn.api?.id) {
        addEntry(key, conn.api.id, 'shared');
      }
    }
  }

  if (connections.serviceProviderConnections) {
    for (const [key, conn] of Object.entries(connections.serviceProviderConnections)) {
      if (conn.serviceProvider?.id) {
        addEntry(key, conn.serviceProvider.id, 'inapp');
      }
    }
  }

  if (connections.agentConnections) {
    for (const [key, conn] of Object.entries(connections.agentConnections)) {
      if (conn.modelProvider?.id) {
        addEntry(key, conn.modelProvider.id, 'agent');
      }
    }
  }

  return { connections: result, renameMap };
}

/**
 * Rewrite connection reference names inside a workflow definition.
 *
 * Uses JSON string replacement to swap deployed keys for canonical template
 * keys in all locations where they appear (action inputs, service provider
 * configs, etc.).
 */
function rewriteConnectionReferences<T>(obj: T, renameMap: Map<string, string>): T {
  if (renameMap.size === 0) return obj;
  let json = JSON.stringify(obj);
  for (const [oldKey, newKey] of renameMap) {
    // Replace all occurrences — keys appear in referenceName and connectionName values
    json = json.split(JSON.stringify(oldKey)).join(JSON.stringify(newKey));
  }
  return JSON.parse(json) as T;
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export async function generateTemplateFromRun(
  opts: TemplateFromRunOptions,
): Promise<AutoTemplate> {
  const {
    runId,
    workflowName,
    workflowDefinition,
    triggerName,
    triggerType,
    runWithOutputLinks,
    fetchContent,
    connections,
    author,
  } = opts;

  // Step 1: Collect mockable action names from the definition
  const mockableActionNames: string[] = [];
  const definitionActions = workflowDefinition.definition?.actions as
    | ActionMap
    | undefined;
  collectMockableActions(definitionActions, mockableActionNames);

  // Step 2: Fetch mock outputs in parallel
  const mocks = await fetchMockOutputs(
    mockableActionNames,
    runWithOutputLinks,
    fetchContent,
  );

  // Step 3: Fetch and scrub trigger outputs
  const triggerOutputs = await fetchTriggerOutputs(
    triggerType,
    runWithOutputLinks,
    fetchContent,
  );

  // Step 4: Collect connection references used in the definition
  const definitionObj = workflowDefinition.definition as Record<string, unknown>;
  const usedConnectionKeys = collectUsedConnectionKeys(definitionObj);

  // Step 5: Extract connections (filtered to this workflow only)
  const { connections: templateConnections, renameMap } = extractConnections(connections, usedConnectionKeys);

  // Step 6: Rewrite connection references in the definition to use canonical
  // keys. The AutoTemplate `workflow` carries only the bare definition — the
  // `{ definition, kind, notes }` wrapper (and canvas sticky notes with it)
  // is not part of the format.
  const rewrittenDefinition = rewriteConnectionReferences(workflowDefinition, renameMap);

  // featuredConnectors (required, min 1): connector ids from the extracted
  // connections, plus the Request built-in when the trigger is HTTP-shaped —
  // which also guarantees connection-less request-triggered workflows satisfy
  // the minimum. Fall back to the Request id as a last resort so validation
  // never fails on an exotic connection-less trigger. Capped at three by
  // priority (serviceProviders > managedApis > connectionProviders) — the
  // create-template dialog offers the full candidate list for adjustment.
  const featuredCandidates = [
    ...(HTTP_TRIGGER_TYPES.has(triggerType.toLowerCase()) ? ['connectionProviders/request'] : []),
    ...Object.values(templateConnections).map((c) => c.apiId),
  ];
  if (featuredCandidates.length === 0) {
    featuredCandidates.push('connectionProviders/request');
  }
  const featuredConnectors = selectDefaultFeaturedConnectors(featuredCandidates);

  // Step 7: Assemble AutoTemplate
  const template = {
    kind: 'AutoTemplate' as const,
    apiVersion: 'v1' as const,
    metadata: {
      id: toKebabCase(workflowName),
      name: workflowName,
      description: `Generated from run ${runId}`,
      category: [...DEFAULT_AUTHORED_TEMPLATE_CATEGORIES],
      author: author?.trim() || 'n/a',
      source: 'community',
      tags: [],
      featuredConnectors,
    },
    workflow: { definition: rewrittenDefinition.definition },
    connections: templateConnections,
    trigger: {
      name: triggerName,
      outputs: triggerOutputs,
    },
    mocks,
  };

  // Step 8: Generalize workflow name → #workflowname# placeholder
  const generalizedTemplate = generalizeWorkflowName(template, workflowName);

  // Step 9: Validate
  const validation = validateAutoTemplate(generalizedTemplate);
  if (!validation.success) {
    throw new Error(
      `Template validation failed:\n${validation.errors.join('\n')}`,
    );
  }

  return validation.data;
}
